import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const LeaveManagerSettings = ({ team, refreshTeamData }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBalances, setUserBalances] = useState({});
  const [showAuditLog, setShowAuditLog] = useState(false);
  
  // Form state for new adjustment
  const [newAdjustment, setNewAdjustment] = useState({
    userId: '',
    leaveTypeId: '',
    adjustmentDays: 0,
    reason: ''
  });

  // Fetch team members and existing adjustments.
  // Leave types are fetched per-user on selection (below) because the
  // effective types depend on the user's assigned policy, not the team.
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const membersResponse = await axios.get('/api/teams/members');
        const activeMembers = membersResponse.data.filter(member => !member.isPendingInvitation);
        setTeamMembers(activeMembers);

        const adjustmentsResponse = await axios.get('/api/leave-adjustments');
        setAdjustments(adjustmentsResponse.data);
      } catch (error) {
        console.error('Error fetching leave manager data:', error);
        toast.error('Failed to load leave manager data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [team]);
  
  // Fetch user balances when a user is selected
  useEffect(() => {
    const fetchUserBalances = async () => {
      if (!selectedUser) {
        setUserBalances({});
        setLeaveTypes([]);
        return;
      }

      try {
        // Response shape: { balances: { [typeId]: {...} }, leaveTypes: [...] }
        // leaveTypes is the user's effective types (policy-aware).
        const balancesResponse = await axios.get(`/api/users/${selectedUser.id}/leave-balances`);
        setUserBalances(balancesResponse.data.balances || {});
        setLeaveTypes(balancesResponse.data.leaveTypes || []);

        setNewAdjustment(prev => ({
          ...prev,
          userId: selectedUser.id,
          leaveTypeId: ''
        }));
      } catch (error) {
        console.error('Error fetching user balances:', error);
        toast.error('Failed to load user leave balances');
      }
    };

    fetchUserBalances();
  }, [selectedUser]);
  
  // Handle user selection
  const handleUserSelect = (userId) => {
    const selected = teamMembers.find(member => member.id === userId);
    setSelectedUser(selected);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAdjustment(prev => ({
      ...prev,
      [name]: name === 'adjustmentDays' ? parseFloat(value) : value
    }));
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!newAdjustment.userId || !newAdjustment.leaveTypeId || !newAdjustment.adjustmentDays) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    try {
      // Submit the adjustment
      const response = await axios.post('/api/leave-adjustments', newAdjustment);
      
      // Update the adjustments list
      setAdjustments(prev => [response.data.adjustment, ...prev]);
      
      // Reset form
      setNewAdjustment({
        userId: selectedUser ? selectedUser.id : '',
        leaveTypeId: '',
        adjustmentDays: 0,
        reason: ''
      });
      
      // Show success message
      toast.success('Leave balance adjusted successfully');
      
      // Refresh user balances
      if (selectedUser) {
        const balancesResponse = await axios.get(`/api/users/${selectedUser.id}/leave-balances`);
        setUserBalances(balancesResponse.data);
      }
    } catch (error) {
      console.error('Error submitting leave adjustment:', error);
      toast.error(error.response?.data?.message || 'Failed to adjust leave balance');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Get leave type name by ID
  const getLeaveTypeName = (typeId) => {
    // Check if the type exists in the team's leave types
    const leaveType = leaveTypes.find(type => type.id === typeId);
    if (leaveType) {
      return leaveType.name;
    }
    
    // If not found in team's leave types, return a formatted version of the ID
    // This is a fallback for cases where the leave type might have been deleted
    return typeId.charAt(0).toUpperCase() + typeId.slice(1).replace(/([A-Z])/g, ' $1');
  };
  
  // Get leave type color by ID
  const getLeaveTypeColor = (typeId) => {
    // Check if the type exists in the team's leave types
    const leaveType = leaveTypes.find(type => type.id === typeId);
    if (leaveType && leaveType.color) {
      return leaveType.color;
    }
    
    // Default color if not found
    return '#6B7280';
  };
  
  // Filter adjustments for the selected user
  const userAdjustments = selectedUser
    ? adjustments.filter(adj => adj.userId === selectedUser.id)
    : [];
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-8">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Leave Adjustments</h2>
        <p className="text-gray-600 dark:text-gray-300 mb-6">
          Adjust leave balances for team members. All adjustments are logged for audit purposes.
        </p>
        
        {/* User selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select Team Member
          </label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={selectedUser ? selectedUser.id : ''}
            onChange={(e) => handleUserSelect(e.target.value)}
          >
            <option value="">Select a team member</option>
            {teamMembers.map(member => (
              <option key={member.id} value={member.id}>
                {member.firstName} {member.lastName}
              </option>
            ))}
          </select>
        </div>
        
        {/* User leave balances */}
        {selectedUser && Object.keys(userBalances).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">
              Current Leave Balances for {selectedUser.firstName} {selectedUser.lastName}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(userBalances).map(([typeId, balance]) => (
                <div 
                  key={typeId} 
                  className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md border-l-4"
                  style={{ borderLeftColor: getLeaveTypeColor(typeId) }}
                >
                  <div className="font-medium">{getLeaveTypeName(typeId)}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Limit: {balance.limit} days
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    Used: {balance.used} days
                  </div>
                  {balance.adjustment !== 0 && (
                    <div className={`text-sm ${balance.adjustment > 0 ? 'text-green-500' : 'text-red-500'}`}>
                      Adjustment: {balance.adjustment > 0 ? '+' : ''}{balance.adjustment} days
                    </div>
                  )}
                  <div className="font-bold mt-1">
                    Remaining: {balance.remaining} days
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Adjustment form */}
        {selectedUser && (
          <form onSubmit={handleSubmit} className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Adjust Leave Balance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Leave Type
                </label>
                <select
                  name="leaveTypeId"
                  value={newAdjustment.leaveTypeId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  <option value="">Select leave type</option>
                  {/* Show all leave types from team settings */}
                  {leaveTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Adjustment Days
                </label>
                <div className="flex items-center">
                  <input
                    type="number"
                    name="adjustmentDays"
                    value={newAdjustment.adjustmentDays}
                    onChange={handleInputChange}
                    step="0.5"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    required
                  />
                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                    (use negative for deduction)
                  </span>
                </div>
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason (optional)
              </label>
              <textarea
                name="reason"
                value={newAdjustment.reason}
                onChange={handleInputChange}
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              ></textarea>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Apply Adjustment'}
              </button>
            </div>
          </form>
        )}
        
        {/* Toggle audit log */}
        {selectedUser && userAdjustments.length > 0 && (
          <div className="mb-4">
            <button
              type="button"
              onClick={() => setShowAuditLog(!showAuditLog)}
              className="flex items-center text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <span className="mr-2">
                {showAuditLog ? 'Hide' : 'Show'} Adjustment History
              </span>
              <svg 
                className={`w-4 h-4 transition-transform ${showAuditLog ? 'transform rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}
        
        {/* Audit log */}
        {selectedUser && showAuditLog && userAdjustments.length > 0 && (
          <div className="border rounded-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Leave Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Adjustment
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Admin
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {userAdjustments.map(adjustment => (
                  <tr key={adjustment.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(adjustment.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span 
                        className="inline-block w-3 h-3 rounded-full mr-2"
                        style={{ backgroundColor: getLeaveTypeColor(adjustment.leaveTypeId) }}
                      ></span>
                      {getLeaveTypeName(adjustment.leaveTypeId)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${adjustment.adjustmentDays > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                      {adjustment.adjustmentDays > 0 ? '+' : ''}{adjustment.adjustmentDays} days
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                      {adjustment.reason || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {adjustment.adminName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* No user selected message */}
        {!selectedUser && (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            Select a team member to view and adjust their leave balances
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveManagerSettings;
